import "react-day-picker/lib/style.css";

import * as _ from "lodash";
import * as moment from "moment-timezone";
import * as React from "react";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";
import { useMappedState } from "redux-react-hook";

import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { isSameDay } from "src/library/utils/date-helpers";
import { breakpoint } from "src/library/utils/styled-media-queries";
import { Icons } from "src/library/components/Icon/types";
import { getOrderStatusFromId } from "src/core/orders/utils/orderStatus";
import {
  IOrder,
  ListOrdersByDropParams,
  OrderOrigin,
  OrdersResponse,
  ordersApi
} from "src/core/orders/api/orders.api";
import { CreateOrderContext } from "src/core/shared/components/HeaderContext/CreateOrderContext";
import { useOrder } from "src/core/orders/hooks";
import { useDrop } from "src/core/drops/hooks";
import { PaginatedParams } from "src/library/utils";
import { IStore } from "src/core/shared/store";

import Day, { DayDetails } from "../OrdersCalendar/Day";
import { WeekDayHeaders } from "../OrdersCalendar/WeekDayHeaders";

export interface IOrdersWeekSummaryProps {
  date: Date;
  week: Date;
}

export interface WarningDates {
  component: JSX.Element;
  date: Date;
}
export interface DayWithDetail {
  details: DayDetails[];
  date: Date;
}

const defaultPagination: PaginatedParams<IOrder> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

const OrdersWeekSummaryDashboardWidget: React.ReactFCWithChildren<IOrdersWeekSummaryProps> = props => {
  const wrapperRef = React.createRef<HTMLDivElement>();
  const [ week, setWeek ] = React.useState<Date>(props.week);
  const [ datesWithDetail, setDatesWithDetail ] = React.useState<DayWithDetail[]>([]);
  const [ { isCreatingOrder }, { createOrder } ] = useOrder();
  const [ { drop } ] = useDrop();
  const history = useHistory();
  const [ ordersResponse, getOrders ] = ordersApi<OrdersResponse>("LIST_DROP_ORDERS");
  const ordersData = ordersResponse.data?.orders.result;
  const dropId = React.useMemo(() => drop ? drop.id : null, [ drop ]);
  const [ sizeClassName, setSizeClassName ] = React.useState("large");

  const { orderStatuses } = useMappedState(({ global }: IStore) => ({
    orderStatuses: global.settings ? global.settings.orderStatuses : [],
    installedModules: global.settings ? global.settings.installedModules : []
  }));

  const handleCalendarDayPick = React.useCallback((date: Date, detail?: DayWithDetail) => {
    if (detail) {
      let isDayMultiOrder: boolean = detail.details.length > 1;

      isDayMultiOrder = isDayMultiOrder || detail.details.some(orderData => orderData.qty > 1);

      if (!isDayMultiOrder) {
        history.push(`/orders/${detail.details[ 0 ].id}`);
      } else {
        history.push(`/orders?filters=deliveryDate:${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`, { ordersViewType: "table" });
      }
    }
  }, [ history ]);

  React.useEffect(() => {
    if (dropId) {
      getDataForCalandar(new Date());
    }
  }, [ dropId ]);

  const getDataForCalandar = React.useCallback(
    (week: Date) => {
      if (drop) {
        getOrders<ListOrdersByDropParams>({
          pagination: {
            ...defaultPagination,
            limit: 100
          },
          filters: {
            orderDeliveryDate: {
              $between: [
                moment
                  .utc(week)
                  .startOf("isoWeek")
                  .toDate(),
                moment
                  .utc(week)
                  .endOf("isoWeek")
                  .toDate()
              ]
            }
          },
          dropId: drop.id
        });
      }
    },
    [ defaultPagination, drop ]
  );

  const orderToDetailDate = React.useCallback(
    (order: IOrder): DayWithDetail | null => {
      const orderStatus = getOrderStatusFromId(orderStatuses, order.orderStatusId);

      if (orderStatus && order.orderDeliveryDate) {
        return {
          date: new Date(order.orderDeliveryDate),
          details: [
            {
              status: orderStatus,
              qty: 1,
              id: order.id
            }
          ]
        };
      }

      return null;
    },
    [ orderStatuses ]
  );

  React.useEffect(() => {
    const detailDates: DayWithDetail[] = [];

    if (ordersData) {
      for (const order of ordersData) {
        if (order.orderDeliveryDate) {
          // @ts-ignore - we check that order.orderDeliveryDate isn't null
          const dateIndex = _.findIndex(detailDates, day => isSameDay(day.date, new Date(order.orderDeliveryDate)));
          const detailDate = orderToDetailDate(order);

          if (detailDate) {
            if (dateIndex === -1) {
              detailDates.push(detailDate);
            } else {
              if (detailDate.details[ 0 ]) {
                const typeIndex = _.findIndex(
                  detailDates[ dateIndex ].details,
                  detail => detail.status.settings.group === detailDate.details[ 0 ].status.settings.group
                );

                if (typeIndex === -1) {
                  detailDates[ dateIndex ].details.push(detailDate.details[ 0 ]);
                } else {
                  detailDates[ dateIndex ].details[ typeIndex ].qty++;
                }
              }
            }
          }
        }
      }
    }

    setDatesWithDetail(detailDates);
  }, [ ordersData ]);

  const thisWeekStart = moment.utc(week).startOf("isoWeek");

  const daysOfThisWeek = _.map(Array(7), (_, i) => {
    return moment
      .utc(week)
      .startOf("isoWeek")
      .add(i, "day")
      .toDate();
  });

  const thisWeekEnd = moment.utc(week).endOf("isoWeek");

  const handleWeekChange = (week: Date) => {
    setWeek(week);
    getDataForCalandar(week);
  };

  //sets a class based on the size of the widget so we can style on this instead of media queries
  React.useEffect(() => {
    if (wrapperRef.current) {
      const { width } = wrapperRef.current.getBoundingClientRect();

      if (width > 550) {
        setSizeClassName("large");
      } else {
        setSizeClassName("small");
      }
    }
  }, [ wrapperRef ]);

  return (
    <Wrapper
      className={sizeClassName}
      ref={wrapperRef}
    >
      <WeekHeader className={`header-${sizeClassName}`}>
        <WeekNavigation>
          <ThisWeek>
            <Text
              className="week-dates-description"
              fontSize="h3"
            >
              <b>
                {thisWeekStart.format("DD")}
              </b>

              {" "}

              {thisWeekStart.format("MMMM")}

              {" "}
              -

              {" "}

              <b>
                {thisWeekEnd.format("DD")}
              </b>

              {" "}

              {thisWeekEnd.format("MMMM YYYY")}
            </Text>
          </ThisWeek>

          <NavArrowWrapper
            onClick={() =>
              handleWeekChange(
                moment
                  .utc(thisWeekStart)
                  .subtract(1, "week")
                  .toDate())}
          >
            <Icon icon={Icons.CHEVRONLEFT} />
          </NavArrowWrapper>

          <NavArrowWrapper
            onClick={() =>
              handleWeekChange(
                moment
                  .utc(week)
                  .add(1, "week")
                  .toDate())}
          >
            <Icon icon={Icons.CHEVRONRIGHT} />
          </NavArrowWrapper>

        </WeekNavigation>

        <div className="action-buttons">
          <CreateOrderContext
            direction="left"
            drop={drop}
            loading={isCreatingOrder}
            createDraftOrder={(deliveryDate, options) => createOrder({
              date: deliveryDate,
              origin: OrderOrigin.Manual,
              orderVariantId: options?.orderVariant?.id
            })}
          />

        </div>

      </WeekHeader>

      <WeekDayHeaders />

      <Week className={`week-${sizeClassName}`}>
        {_.map(daysOfThisWeek, (date, i) => {
          const dayDetail = _.find(datesWithDetail, detail => isSameDay(date, detail.date));
          let modifier = dayDetail ? "Day Day--hasInfo " : "Day";

          dayDetail &&
            _.each(dayDetail.details, detail => {
              modifier += `Day--${detail.status.settings.group} `;
            });

          return (
            <Day
              showMonth
              key={`day-${date}-${i}`}
              className={modifier}
              handleDayClick={() => handleCalendarDayPick(date, dayDetail)}
              loading={ordersResponse.pending}
              date={date}
              details={dayDetail && dayDetail.details}
            />
          );
        })}
      </Week>
    </Wrapper>
  );
};

const Week = styled.div`
  ${({ theme }) => css`
    width: 100%;
    flex-direction: column;
    display: flex;
    flex: 1 0 14.2857%;
    padding: 0 ${theme.spacing.padding.sm};

    .Day {
      width: 100%;
    }
    
    &.week-large {
      flex-direction: row;
      padding: 0 ${theme.spacing.padding.md};
    }

    &.week-small {
      > div {
        ${breakpoint("lg", theme)} {
          padding: ${theme.spacing.padding.xs};
          margin: 5px 2px;
          min-height: 80px;
        }
      }
    }
  `}
`;

const Wrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;  
    height: 100%;
    display: flex;
    flex-direction: column;
    margin-top: ${theme.spacing.margins.lg};
    background: ${theme.colors.white};
    overflow: hidden;

    .week-icon-wrapper {
      flex-direction: column;
    }

    .week-day-headers {
      display: none;
    }

    &.large {
      margin-top: 0;

      .week-day-headers {
        display: flex;
      }

      .week-icon-wrapper {
        flex-direction: column;
      }
    }
  `}
`;

const WeekHeader = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: column;
    padding: ${theme.spacing.padding.sm};
    margin-bottom: ${theme.spacing.margins.sm};

    .action-buttons {
      display: flex;
      flex-wrap: wrap;
       
      > div {
        margin-left: ${theme.spacing.margins.md};
        margin-bottom: ${theme.spacing.margins.xs};
      }
    }

    &.header-large {
      flex-direction: row;
      padding: ${theme.spacing.padding.md};
      border-bottom: ${theme.colors.lightGrey} 1px solid;

      .action-buttons {
        justify-content: flex-end;
      }
    }
  `}
`;

const WeekNavigation = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: ${theme.spacing.margins.md};

    ${breakpoint("md", theme)} {
      margin-bottom: 0;
    }
  `};
`;

const NavArrowWrapper = styled.div`
  display: flex;
`;

const ThisWeek = styled.div`
  ${({ theme }) => css`
    margin-right: ${theme.spacing.margins.md}
    /* To account for longest month names. This ensures buttons don't move around whilst clicking through months */
    width: 305px;
    text-align: center;

    b {
      font-weight: bold;
    }
  `}
`;

export default OrdersWeekSummaryDashboardWidget;
